// The Trail (PvE trainer path, season one; design note mythic-idle-design/trail-and-held-items.md): one path per
// region, opened from the world map. Pure rules and data shared by the server, the client, the bots and tests.
//
// - Each path is a line of trainer nodes and ends at its region's boss. Trainer nodes open in order, each at a trainer
//   level inside the region's band (the first at Lv 10). A trainer fields a fixed line-up (one to five creatures of
//   the region, set levels and rolls), steadily stronger along the path, each built round one idea.
//   Line-up levels sit below the node's level: they were tuned against the bots' best PvP line-ups at that level
//   (one strong lead and a weaker bench), from about 95% wins at the first node to about 45% at the last trainer,
//   with synergies on both sides; the region-synergy trainers carry better rolls so levels still rise along a path.
// - Trainer fights are PvP fights (shared/arena.ts battle(), 'arena' mode, so held items and synergies count), fully auto, on a
//   seed the server picks; the result replays from that seed and the line-up as it fought (TrailState.last). The hunt
//   carries on. The first win pays the node's reward once per player; rematches pay nothing. Two trainers on each
//   path pay held-item pieces, ten of one item between them (Wolfhide Cloak, Winged Sandals, Fox Lantern): one forge.
//   On the Frost Road they are the second and third, so the first forge (Lv 35) is ready as ranked opens (Lv 40);
//   on the later paths the third and sixth.
// - A boss node is the region boss as it always was (shared/boss-duel.ts, the key, the rules, the Boss Ladder): the
//   Trail is where its card, estimate, ladder and crafting now live. It needs a key and every trainer on its path
//   cleared (bossProblem; a boss already beaten stays open). Its first win pays one whole held item from the boss's pool and CONFIG.trail.bossDiamonds diamonds, once.
//   From then on boss wins and that boss's Boss Ladder steps can drop pieces (shared/held.ts).
// - Later regions are data: add a path to TRAIL.
import { CONFIG } from './config.ts';
import type { ArenaCreature, BattleResult } from './arena.ts';
import type { BoardRow } from './board.ts';
import { pvpBattle } from './pvpfight.ts';
import { killCoins } from './xp.ts';
import { heldPiece } from './held.ts';
import { SPECIES, type RegionId } from './species.ts';
import { Rng } from './rng.ts';
import type { Rolls } from './rolls.ts';

/** Trail fights open at this trainer level (shared/unlocks.ts 'trail'). */
export const TRAIL_LEVEL = 10;

/** One creature of a trail trainer: species, level, and its roll (quality, all six talents, bloodline, trait, stars). */
export interface TrailMon { s: string; l: number; q?: number; iv?: number; bl?: number; tr?: string; st?: number }

export interface TrailReward { coins: number; items: { item: string; qty: number }[] }

/** What a trainer's line-up is built to show. */
export type TrailIdea = 'pair' | 'speed' | 'wall' | 'counter' | 'synergy' | 'champion';
export const IDEA_NAME: Record<TrailIdea, string> = {
  pair: 'First duel', speed: 'Speed team', wall: 'Tank wall', counter: 'Counter trap', synergy: 'Region synergy', champion: 'Champion',
};

export interface TrailTrainer {
  kind: 'trainer';
  id: string;
  region: RegionId;
  /** Trainer level that opens it (and the previous trainer node cleared). */
  level: number;
  name: string;
  /** Worn code (shared/looks.ts wornCode): outfit and look. Draw with the player icon, never OUTFIT[x] directly. */
  outfit: string;
  idea: TrailIdea;
  /** One line of flavour. */
  line: string;
  team: TrailMon[];
  /** Rows on the board, one per creature of team (a placement that teaches the board); absent: defaultRows. */
  rows?: BoardRow[];
  reward: TrailReward;
}
export interface TrailBoss {
  kind: 'boss';
  id: string;
  region: RegionId;
  boss: string;
  /** The boss's level, shown on the node; the fight opens by the old rules (a key in the bag). */
  level: number;
}
export type TrailNode = TrailTrainer | TrailBoss;
export interface TrailPath { region: RegionId; name: string; nodes: TrailNode[] }

/** A player's Trail record (TrainerDTO.trail). */
export interface TrailState {
  /** First clears: node id -> when. Each pays once. */
  cleared: Record<string, number>;
  /** The latest trainer-node fight, so it can be watched again: node, seed, result, and the line-up as it fought. */
  last?: TrailLast;
}
/**
 * rows: the player's rows as sent (front or back per creature of team; absent: defaultRows). board: the board rules
 * version it was fought on (BattleResult.board; 0: fronts in order, the board switched off), so it replays on them;
 * absent (fights saved before 2026-10-20): replayed on the live fight.
 */
export interface TrailLast { node: string; seed: number; win: boolean; at: number; team: ArenaCreature[]; rows?: BoardRow[]; board?: number }

/** A trainer fight as the server sends it ({ t: 'trail', fight }). */
export interface TrailFightDTO { node: string; seed: number; win: boolean; first: boolean; reward: TrailReward | null; result: BattleResult }

// ---------- rewards ----------
/** Coins for a first clear: this many kills' worth at the node's level. */
export const TRAIL_COIN_KILLS = 60;
const coins = (level: number) => killCoins(level) * TRAIL_COIN_KILLS;
const it = (item: string, qty: number) => ({ item, qty });
/** A node's reward: coins at its level and the given items. */
const pay = (level: number, ...items: { item: string; qty: number }[]): TrailReward => ({ coins: coins(level), items });

// ---------- the paths ----------
const T = (x: Omit<TrailTrainer, 'kind' | 'reward'> & { items: { item: string; qty: number }[] }): TrailTrainer => {
  const { items, ...rest } = x;
  return { kind: 'trainer', ...rest, reward: pay(x.level, ...items) };
};

export const TRAIL: TrailPath[] = [
  {
    region: 'midgard', name: 'The Frost Road',
    nodes: [
      T({ id: 'mg1', region: 'midgard', level: 10, name: 'Ylva the Herder', outfit: 'herder~s1h3c4', idea: 'pair',
        line: 'My pups have chased every sled on the fjord. Yours next?',
        team: [{ s: 'snowpup', l: 8 }, { s: 'nutling', l: 7 }],
        items: [it('silver-seal', 10), it('mead', 5)] }),
      T({ id: 'mg2', region: 'midgard', level: 20, name: 'Hakon Swiftfoot', outfit: 'ranger~s2h1c0', idea: 'speed',
        line: 'Blink and the fight is over. Mine usually blink first.',
        team: [{ s: 'ratatosk', l: 15, tr: 'nimble' }, { s: 'stormraven', l: 14, tr: 'nimble' }, { s: 'snowhare', l: 14, tr: 'nimble' }],
        items: [it('silver-seal', 15), it('mead', 5), it('froststone', 1), it(heldPiece('wolfhide'), 4)] }),
      T({ id: 'mg3', region: 'midgard', level: 35, name: 'Old Gunnhild', outfit: 'jarl~s0h2c5', idea: 'wall',
        line: 'Hit my trolls all you like. They were here before the mountain.',
        team: [{ s: 'bergtroll', l: 28, tr: 'sturdy' }, { s: 'draugr', l: 26, tr: 'sturdy' }, { s: 'cragbuck', l: 26, tr: 'hardy' }, { s: 'skerryseal', l: 25, tr: 'sturdy' }],
        rows: ['front', 'front', 'front', 'back'],
        items: [it('gold-seal', 10), it('mead', 10), it('cragstone', 1), it(heldPiece('wolfhide'), 6)] }),
      T({ id: 'mg4', region: 'midgard', level: 50, name: 'Brokk of the Forge', outfit: 'ember~s3h1c3', idea: 'counter',
        line: 'Half of Midgard fights with frost. I brought the fire.',
        team: [{ s: 'muspelbrute', l: 46, tr: 'flame-touched' }, { s: 'forgebeard', l: 46, tr: 'flame-touched' }, { s: 'thunderhorn', l: 45, tr: 'fierce' }, { s: 'emberling', l: 44, tr: 'flame-touched' }],
        items: [it('gold-seal', 15), it('mead', 10), it('flamestone', 2)] }),
      T({ id: 'mg5', region: 'midgard', level: 65, name: 'Ragna of the Wild Hunt', outfit: 'keeper~s1h2c0', idea: 'synergy',
        line: 'When the winter hunt rides, every beast in the north rides with it.',
        team: [{ s: 'fenrisborn', l: 52, tr: 'frost-touched' }, { s: 'barrowking', l: 51, tr: 'frost-touched' }, { s: 'nightwarden', l: 51 }, { s: 'gardvord', l: 50 }, { s: 'hrimthurs', l: 50, tr: 'sturdy' }],
        items: [it('mythril-seal', 10), it('mead', 15), it('shadestone', 1), it('froststone', 1)] }),
      T({ id: 'mg6', region: 'midgard', level: 80, name: 'Jarl Eirik Stormbeard', outfit: 'jarl~s1h0c3', idea: 'champion',
        line: 'Beat me and only the wolf stands between you and the south.',
        team: [{ s: 'jormunspawn', l: 61, q: 1.05, iv: 18 }, { s: 'nidhogg', l: 60, q: 1.05, iv: 18 }, { s: 'valkyrie', l: 60, q: 1.05, iv: 18, tr: 'fierce' }, { s: 'gullinbursti', l: 59, iv: 18 }, { s: 'muninraven', l: 59, iv: 18 }],
        rows: ['back', 'front', 'back', 'front', 'back'],
        items: [it('mythril-seal', 15), it('nectar', 10), it('lightstone', 1), it('stormstone', 1)] }),
      { kind: 'boss', id: 'mg-boss', region: 'midgard', boss: 'fenrir', level: 100 },
    ],
  },
  {
    region: 'hellas', name: "The Hero's Road",
    nodes: [
      T({ id: 'hl1', region: 'hellas', level: 150, name: 'Kallisto the Shepherd', outfit: 'herder~s2h3c4', idea: 'pair',
        line: 'My flock has seen off wolves. You will need more than a wolf.',
        team: [{ s: 'goldfleece', l: 140 }, { s: 'satyr', l: 140 }],
        items: [it('gold-seal', 20), it('nectar', 5), it('lightstone', 1)] }),
      T({ id: 'hl2', region: 'hellas', level: 250, name: 'Phaedra Windrunner', outfit: 'wayfarer~s1h3c3', idea: 'speed',
        line: 'The wind off Olympus is fast. I am faster.',
        team: [{ s: 'stormharpy', l: 315, tr: 'nimble' }, { s: 'myrmidon', l: 310, tr: 'nimble' }, { s: 'starsteed', l: 277, tr: 'nimble' }],
        items: [it('mythril-seal', 15), it('nectar', 10), it('stormstone', 1)] }),
      T({ id: 'hl3', region: 'hellas', level: 380, name: 'Krateros the Mason', outfit: 'hoplite~s3h1c0', idea: 'wall',
        line: 'Nobody has found the way out of my labyrinth. Nobody has broken through it either.',
        team: [{ s: 'labyrinthlord', l: 251, tr: 'sturdy' }, { s: 'karkinos', l: 275, tr: 'sturdy' }, { s: 'talos', l: 275, tr: 'hardy' }, { s: 'groveelder', l: 272, tr: 'sturdy' }],
        rows: ['front', 'front', 'front', 'back'],
        items: [it('mythril-seal', 20), it('nectar', 10), it('cragstone', 1), it(heldPiece('wingedsandals'), 4)] }),
      T({ id: 'hl4', region: 'hellas', level: 500, name: 'Ione of the Deep', outfit: 'scholar~s4h2c6', idea: 'counter',
        line: 'Every hero walks in the light. My friends prefer the dark.',
        team: [{ s: 'lamia', l: 353, tr: 'shade-touched' }, { s: 'empusa', l: 351, tr: 'shade-touched' }, { s: 'cerberus', l: 351, tr: 'shade-touched' }, { s: 'deepsiren', l: 347 }],
        items: [it('mythril-seal', 25), it('nectar', 15), it('shadestone', 1), it('tidestone', 1)] }),
      T({ id: 'hl5', region: 'hellas', level: 620, name: 'Aristeia the Bright', outfit: 'hoplite~s1h4c4', idea: 'synergy',
        line: 'The sun chariot never runs late, and neither do we.',
        team: [{ s: 'chrysomallos', l: 394, tr: 'light-touched', q: 1.05, iv: 18 }, { s: 'athenowl', l: 392, tr: 'light-touched', q: 1.05, iv: 18 }, { s: 'starsteed', l: 335, q: 1.05, iv: 18 }, { s: 'griffinlord', l: 389, tr: 'light-touched', q: 1.05, iv: 18 }, { s: 'phoenix', l: 389, q: 1.05, iv: 18 }],
        items: [it('mythril-seal', 30), it('nectar', 15), it('lightstone', 2)] }),
      T({ id: 'hl6', region: 'hellas', level: 720, name: 'Theron the Twice-Thrown', outfit: 'ember~s2h1c2', idea: 'champion',
        line: 'Typhon has thrown me down twice. Show me you can do better.',
        team: [{ s: 'lernaeanhydra', l: 333, q: 1.05, iv: 18 }, { s: 'medusa', l: 331, q: 1.05, iv: 18 }, { s: 'scylla', l: 331, q: 1.05, iv: 18 }, { s: 'nemeanlion', l: 329, iv: 18 }, { s: 'phoenix', l: 329, iv: 18 }],
        rows: ['front', 'back', 'front', 'front', 'back'],
        items: [it('mythril-seal', 40), it('nectar', 20), it('tidestone', 1), it('flamestone', 1), it(heldPiece('wingedsandals'), 6)] }),
      { kind: 'boss', id: 'hl-boss', region: 'hellas', boss: 'typhon', level: 800 },
    ],
  },
  {
    region: 'yomi', name: 'The Lantern Road',
    nodes: [
      T({ id: 'ym1', region: 'yomi', level: 900, name: 'Hoshiko the Lamplighter', outfit: 'wayfarer~s1h2c7', idea: 'pair',
        line: 'Follow the lanterns, and mind the ones that follow you.',
        team: [{ s: 'onibi', l: 880 }, { s: 'monsoonparasol', l: 880 }],
        items: [it('mythril-seal', 40), it('nectar', 20), it('flamestone', 1)] }),
      T({ id: 'ym2', region: 'yomi', level: 1150, name: 'Kaito of the Gale', outfit: 'ranger~s2h1c6', idea: 'speed',
        line: 'The wind cuts before you see it move.',
        team: [{ s: 'galeblade', l: 1081, tr: 'nimble' }, { s: 'raiju', l: 1071, tr: 'nimble' }, { s: 'monsoonparasol', l: 1071, tr: 'nimble' }, { s: 'nekomata', l: 1061, tr: 'nimble' }],
        items: [it('mythril-seal', 50), it('nectar', 25), it('stormstone', 1)] }),
      T({ id: 'ym3', region: 'yomi', level: 1400, name: 'Iwao the Gatekeeper', outfit: 'keeper~s3h1c0', idea: 'wall',
        line: 'This gate has stood a thousand years. It is not opening for you.',
        team: [{ s: 'shisa', l: 1431, tr: 'sturdy' }, { s: 'earthshaker', l: 1420, tr: 'sturdy' }, { s: 'gashadokuro', l: 1420, tr: 'hardy' }, { s: 'reveriecolossus', l: 1411, tr: 'sturdy' }],
        rows: ['front', 'front', 'front', 'back'],
        items: [it('mythril-seal', 50), it('nectar', 25), it('cragstone', 1), it(heldPiece('foxlantern'), 4)] }),
      T({ id: 'ym4', region: 'yomi', level: 1650, name: 'Lady Tsuyu', outfit: 'scholar~s0h2c0', idea: 'counter',
        line: 'Your garden grows well. It also burns well.',
        team: [{ s: 'ninefold', l: 1344, tr: 'flame-touched' }, { s: 'redhornwarlord', l: 1496, tr: 'flame-touched' }, { s: 'nekomata', l: 1496, tr: 'flame-touched' }, { s: 'tempestsovereign', l: 1487, tr: 'storm-touched' }],
        items: [it('mythril-seal', 60), it('nectar', 30), it('flamestone', 2)] }),
      T({ id: 'ym5', region: 'yomi', level: 1900, name: 'Yoru the Herald', outfit: 'ronin~s1h0c6', idea: 'synergy',
        line: 'When the night parade passes, nobody stands in the road.',
        team: [{ s: 'jorogumo', l: 1240, tr: 'shade-touched', q: 1.05, iv: 18 }, { s: 'willowshade', l: 1236, tr: 'shade-touched', q: 1.05, iv: 18 }, { s: 'rokurokubi', l: 1233, tr: 'shade-touched', q: 1.05, iv: 18 }, { s: 'umibozu', l: 1381, q: 1.05, iv: 18 }, { s: 'nue', l: 1229, tr: 'shade-touched', q: 1.05, iv: 18 }],
        items: [it('mythril-seal', 70), it('nectar', 35), it('shadestone', 2)] }),
      T({ id: 'ym6', region: 'yomi', level: 2100, name: 'Master Ren', outfit: 'ronin~s2h1c5', idea: 'champion',
        line: 'Hachimata has eight heads. You only have to get past one of me.',
        team: [{ s: 'tempestsovereign', l: 1344, q: 1.05, iv: 18 }, { s: 'redhornwarlord', l: 1340, q: 1.05, iv: 18 }, { s: 'ninefold', l: 1196, q: 1.05, iv: 18 }, { s: 'gashadokuro', l: 1336, iv: 18 }, { s: 'yukionna', l: 1336, iv: 18 }],
        rows: ['back', 'front', 'back', 'front', 'back'],
        items: [it('mythril-seal', 80), it('nectar', 40), it('froststone', 1), it('tidestone', 1), it(heldPiece('foxlantern'), 6)] }),
      { kind: 'boss', id: 'ym-boss', region: 'yomi', boss: 'hachimata', level: 2300 },
    ],
  },
];

export const TRAIL_NODES: TrailNode[] = TRAIL.flatMap((p) => p.nodes);
export const TRAIL_NODE: Record<string, TrailNode> = Object.fromEntries(TRAIL_NODES.map((n) => [n.id, n]));
export const pathOf = (region: string): TrailPath | undefined => TRAIL.find((p) => p.region === region);
/** The boss node of a boss (where its first clear is recorded). */
export const bossNode = (boss: string): TrailBoss | undefined => TRAIL_NODES.find((n): n is TrailBoss => n.kind === 'boss' && n.boss === boss);
export const isTrailNode = (id: unknown): id is string => typeof id === 'string' && Object.hasOwn(TRAIL_NODE, id);

export const freshTrail = (): TrailState => ({ cleared: {} });
export const cleared = (s: TrailState | undefined, node: string) => !!s?.cleared[node];

/** The trainer node before this one on its path, if any. */
export function prevTrainer(node: TrailNode): TrailTrainer | undefined {
  const p = pathOf(node.region)!;
  const i = p.nodes.indexOf(node);
  for (let j = i - 1; j >= 0; j--) { const n = p.nodes[j]; if (n.kind === 'trainer') return n; }
  return undefined;
}

/**
 * Why a trainer node can't be fought now ('' when it can): the Trail's own level, the node's level, and the trainer
 * node before it cleared. Boss nodes: see bossProblem (and a key in the bag).
 */
export function trailProblem(node: TrailNode, trainerLevel: number, s: TrailState | undefined): string {
  if (node.kind !== 'trainer') return '';
  if (trainerLevel < TRAIL_LEVEL) return `The Trail opens at trainer level ${TRAIL_LEVEL}.`;
  if (trainerLevel < node.level) return `${node.name.split(' ')[0]} fights trainers from Lv ${node.level.toLocaleString('en-US')}.`;
  const prev = prevTrainer(node);
  if (prev && !cleared(s, prev.id)) return `Beat ${prev.name} first.`;
  return '';
}

/**
 * Why a boss can't be fought now ('' when it can): every trainer on its path cleared first. A boss whose node is
 * already cleared stays open, so saves from before this rule keep their bosses. Bosses with no node are always open.
 */
export function bossProblem(boss: string, s: TrailState | undefined): string {
  const node = bossNode(boss);
  if (!node || cleared(s, node.id)) return '';
  const p = pathOf(node.region)!;
  if (p.nodes.some((n) => n.kind === 'trainer' && !cleared(s, n.id))) return `Clear ${p.name.replace(/^The /, 'the ')} first.`;
  return '';
}

/** The node a path shows first: the first trainer node not yet cleared, else the boss. */
export function nextNode(region: string, s: TrailState | undefined): TrailNode | undefined {
  const p = pathOf(region);
  return p?.nodes.find((n) => n.kind === 'trainer' && !cleared(s, n.id)) ?? p?.nodes.at(-1);
}

/** Nodes cleared on a path, and how many it has. */
export function pathProgress(region: string, s: TrailState | undefined): { done: number; of: number } {
  const p = pathOf(region);
  return { done: p ? p.nodes.filter((n) => cleared(s, n.id)).length : 0, of: p?.nodes.length ?? 0 };
}

// ---------- fights ----------
const ivs = (n: number): Rolls['ivs'] => [n, n, n, n, n, n];
/** A trail trainer's line-up as it fights (ids -1, -2, ... so they never clash with a player's creatures). */
export function trailTeam(node: TrailTrainer): ArenaCreature[] {
  return node.team.map((m, i) => ({
    id: -(i + 1), species: m.s, level: m.l, stars: m.st ?? 0, relic: null,
    rolls: { ivs: ivs(m.iv ?? 15), quality: m.q ?? 1, bloodline: m.bl ?? 0, aura: false, trait: m.tr ?? 'sturdy' },
  }));
}

/**
 * One trainer fight: the player's line-up (side 0, on `rows`, else defaultRows) against the node's (side 1, the
 * defender, on its own rows) on this seed, as every PvP fight (shared/pvpfight.ts pvpBattle). `board`: replay on the
 * rules a saved fight was fought on (TrailLast.board); absent, the live fight.
 */
export function trailFight(node: TrailTrainer, team: ArenaCreature[], seed: number, name = 'You', rows?: readonly BoardRow[] | null, board?: number): BattleResult {
  const opts = board === undefined ? {} : board ? { board: true, boardV: board } : { board: false };
  return pvpBattle({ name, team, rows }, { name: node.name, team: trailTeam(node), rows: node.rows }, new Rng(seed), opts);
}

/** A boss node's first clear: diamonds, once (CONFIG.trail.bossDiamonds). */
export const bossClearDiamonds = () => CONFIG.trail.bossDiamonds;
/** Diamonds the whole Trail can ever pay one player. */
export const trailDiamondsTotal = () => TRAIL_NODES.filter((n) => n.kind === 'boss').length * CONFIG.trail.bossDiamonds;

/** Every species a trail trainer fields exists and belongs to its region (checked by tests). */
export function trailDataProblems(): string[] {
  const out: string[] = [];
  for (const n of TRAIL_NODES) {
    if (n.kind !== 'trainer') continue;
    if (!n.team.length || n.team.length > CONFIG.teamSize) out.push(`${n.id}: ${n.team.length} creatures`);
    const seen = new Set<string>();
    for (const m of n.team) {
      const sp = SPECIES[m.s];
      if (!sp) out.push(`${n.id}: no species ${m.s}`);
      else if (sp.region !== n.region) out.push(`${n.id}: ${m.s} is from ${sp.region}`);
      if (seen.has(m.s)) out.push(`${n.id}: ${m.s} twice`);
      seen.add(m.s);
    }
  }
  return out;
}
